import type { Messages } from '../catalogue.en'

/**
 * The external clinical entry: care recorded from outside the clinic, its
 * dedicated page, lifecycle, translations, and the entries an AI processing
 * proposes from patient documents.
 *
 * Composed into the public catalogue by `catalogue.he.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const externalEntry: Partial<Messages> = {
  "externalEntry.approximateDate": "בערך {date}",
  "externalEntry.recordedBy": "הרשומה הוזנה על ידי",
  "externalEntry.title": "רשומה רפואית חיצונית",
  "externalEntry.external": "חיצונית",
  "externalEntry.statusAmending": "בתיקון",
  "externalEntry.careDetails": "פרטי הטיפול",
  "externalEntry.place": "מקום",
  "externalEntry.eventDate": "תאריך הטיפול",
  "externalEntry.dateHint": "אפשר להזין שנה, שנה וחודש או תאריך מלא. שנה וחודש מוצגים כתאריך משוער.",
  "externalEntry.approximate": "משוער",
  "externalEntry.approximateMark": "≈",
  "externalEntry.heading": "כותרת",
  "externalEntry.diagnoses": "אבחנות",
  "externalEntry.diagnosisHint": "לא חובה. כל אבחנה היא קוד מהמילון או הניסוח של המקור. קוד אינו מאשר את האבחנה קלינית; האבחנות נשארות ברשומה החיצונית.",
  "externalEntry.diagnosisReadHint": "התאמת קוד אינה אישור קליני לאבחנה. האבחנות נשארות ברשומה החיצונית.",
  "externalEntry.diagnosesEmpty": "לא צוינו אבחנות.",
  "externalEntry.addDiagnosis": "הוספת אבחנה",
  "externalEntry.diagnosisAddHint": "בחרו קודים בחלונית מילון אבחנות, או כתבו כטקסט אבחנה שאינה במילון.",
  "externalEntry.newDiagnosisText": "אבחנה כטקסט",
  "externalEntry.addDiagnosisText": "הוספה כטקסט",
  "externalEntry.diagnosisText": "טקסט האבחנה",
  "externalEntry.diagnosisAsText": "טקסט",
  "externalEntry.done": "סיום",
  "externalEntry.diagnosisUnsavedIntent": "רשומה רפואית חיצונית — אבחנה שהוקלדה ולא נוספה",
  "externalEntry.doctor": "רופא חיצוני",
  "externalEntry.clinic": "מרפאה חיצונית",
  "externalEntry.findOrAddName": "חיפוש או הוספה",
  "externalEntry.addName": "הוספת \"{name}\"",
  "externalEntry.newNameHint": "שם חדש: יתווסף לקטלוג בעת שמירת הרשומה.",
  "externalEntry.manageName": "שינוי שם או מיזוג בקטלוג",
  "externalEntry.globalNameHint": "השינוי יחול על כל הרשומות הנוכחיות המשתמשות בשם. גרסאות קודמות ישמרו את השם המקורי.",
  "externalEntry.rename": "שינוי שם",
  "externalEntry.mergeInto": "השם שיישאר לאחר המיזוג",
  "externalEntry.merge": "מיזוג",
  "externalEntry.country": "מדינה",
  "externalEntry.city": "עיר",
  "externalEntry.source": "מקור המידע",
  "externalEntry.files": "קובצי המטופל המקושרים לרשומה",
  "externalEntry.filesEmpty": "אין קבצים מקושרים לרשומה זו.",
  "externalEntry.aiPages": "בינה מלאכותית: עמ' {pages}",
  "externalEntry.aiWhole": "בינה מלאכותית: כל הקובץ",
  "externalEntry.viewFile": "פתיחת קובץ",
  "externalEntry.linkFile": "קישור קובץ של המטופל",
  "externalEntry.chooseFile": "בחירת קובץ",
  "externalEntry.link": "קישור",
  "externalEntry.unlink": "ביטול קישור",
  "externalEntry.unlinkFile": "ביטול הקישור של {name}",
  "externalEntry.upload": "העלאה וקישור של קובץ",
  "externalEntry.uploading": "מעלה…",
  "externalEntry.uploadFailed": "לא ניתן להעלות את הקובץ.",
  "externalEntry.filesSavedWithEntry": "קישורי הקבצים נשמרים יחד עם הרשומה. קובץ שהועלה נוסף מיד לקובצי המטופל.",
  "externalEntry.filesLocked": "קישורי הקבצים משתנים רק בטיוטה או בזמן תיקון הרשומה.",
  "externalEntry.remove": "הסרה",
  "externalEntry.saving": "שמירה…",
  "externalEntry.required": "יש להזין תוכן רפואי ולפחות את שנת הטיפול.",
  "externalEntry.loadFailed": "לא ניתן לטעון את הרשומה החיצונית.",
  "externalEntry.writeFailed": "לא ניתן לשמור. יש לבדוק את השדות וההרשאות. במקרה של התנגשות, יש לטעון את הרשומה הנוכחית ולבדוק את הטיוטה.",
  "externalEntry.writeForbidden": "אין לך הרשאה לשנות רשומה זו.",
  "externalEntry.notEditable": "לא ניתן עוד לשנות את הרשומה במצבה הנוכחי. יש לרענן את הדף כדי לראות אותה כפי שהיא.",
  "externalEntry.conflict": "הרשומה שונתה במקום אחר. יש לטעון את הגרסה השמורה כדי להמשיך; שינויים שלא נשמרו יימחקו.",
  "externalEntry.changedElsewhere": "הרשומה שונתה במקום אחר. הגרסה הנוכחית נטענה; יש לבדוק אותה ולנסות שוב.",
  "externalEntry.unsavedIntent": "רשומה רפואית חיצונית — שינויים שלא נשמרו",
  "externalEntry.translationUnsavedIntent": "תרגום רשומה רפואית חיצונית — שינויים שלא נשמרו",
  "externalEntry.completedNotice": "הרשומה החיצונית הושלמה והיא לקריאה בלבד. כדי לשנות אותה יש לפתוח תיקון.",
  "externalEntry.completeConfirmBody": "השלמה הופכת את הרשומה לסופית. שינויים מאוחרים יותר דורשים תיקון עם סיבה.",
  "externalEntry.completeCorrectionConfirmBody": "השלמת התיקון שומרת אותו כגרסה המתוקנת של הרשומה. הגרסה הקודמת נשמרת בהיסטוריה.",
  "externalEntry.amendBody": "בחר את הסיבה. הרשומה תיפתח לעריכה עם אותו תוכן. הגרסה שהושלמה נשמרת בהיסטוריה, והשלמת התיקון שומרת את הגרסה המתוקנת.",
  "externalEntry.mergedReadOnly": "המטופל אוחד עם תיק אחר. הרשומה לקריאה בלבד.",
  "externalEntry.deskOnly": "כאן הרשומה לקריאה בלבד. עריכה, השלמה ותיקון זמינים במחשב.",
  "externalEntry.navDescription": "פרטי הטיפול, פרוטוקול ואבחנות",
  "externalEntry.navProposed": "בינה מלאכותית: רשומה מוצעת",
  "externalEntry.navFiles": "קבצים",
  "externalEntry.navFilesDescription": "המסמכים שמהם נרשמה הרשומה",
  "externalEntry.printUnavailable": "הדפסה של רשומה חיצונית עדיין אינה זמינה.",
  "externalEntry.namesFailed": "לא ניתן לטעון את השמות השמורים. אפשר להזין שם חדש.",
  "externalEntry.loading": "טעינה…",
  "externalEntry.filesFailed": "לא ניתן לטעון את קובצי המטופל.",
  "externalEntry.reload": "טעינה מחדש",
  "externalEntry.reloadFailed": "הטעינה נכשלה. הטיוטה נשמרה בטופס.",
  "externalEntry.reloaded": "הרשומה השמורה נטענה. הטופס מציג את הערכים הנוכחיים שלה.",
  "externalEntry.history": "היסטוריית הרשומה",
  "externalEntry.historyFailed": "לא ניתן לטעון את היסטוריית הרשומה.",
  "externalEntry.earlier": "גרסאות קודמות",
  "externalEntry.versionNotice": "מוצגת גרסה {revision}, שנשמרה ב־{date}.",
  "externalEntry.versionTranslations": "תרגומים:",
  "externalEntry.backToCurrent": "חזרה לרשומה הנוכחית",
  "externalEntry.aiTitle": "רשומות חיצוניות ממסמכים",
  "externalEntry.sourceWarnings": "קיימת אי־ודאות במקור. יש לבדוק את הרשומה מול המסמכים.",
  "externalEntry.summaryGroup": "רשומות רפואיות חיצוניות",
  "externalEntry.fileUnavailable": "הקובץ המקושר אינו זמין.",
  "externalEntry.recognition.included": "ייקראו",
  "externalEntry.recognition.excluded": "לא ייכללו",
  "externalEntry.recognition.reasonUnlinked": "טרם נרשמו כמסמכי המטופל.",
  "externalEntry.recognition.reasonBusy": "כבר כלולים בעיבוד שלא הסתיים.",
  "externalEntry.recognition.openProcessing": "פתיחת העיבוד",
  "externalEntry.recognition.reasonUnreadable": "לא ניתן לקרוא את הטקסט שלהם.",
  "externalEntry.recognition.reasonOverLimit": "עיבוד אחד קורא {limit} מסמכים לכל היותר.",
  "externalEntry.recognition.needsOcr": "נדרש זיהוי טקסט תחילה: {count}",
  "externalEntry.recognition.ocrRefused": "מסמכים אלה זקוקים לזיהוי טקסט, ואין באפשרותך להפעיל אותו: נדרשות הרשאה לעיבוד מסמכים ופונקציית זיהוי טקסט מופעלת.",
  "externalEntry.recognition.pages": "עמודים: {pages} מתוך {limit} לכל היותר",
  "externalEntry.recognition.pagesUnknown": "מסמכים שמספר העמודים שלהם עדיין לא ידוע: {count}",
  "externalEntry.recognition.pagesKnown": "עמודים ידועים עד כה: {pages} מתוך {limit} לכל היותר",
  "externalEntry.recognition.pagesAfterOcr": "מספר העמודים יתברר אחרי זיהוי הטקסט; עיבוד אחד קורא {limit} עמודים לכל היותר.",
  "externalEntry.recognition.pagesOver": "זה יותר עמודים ממה שעיבוד אחד קורא. יש להסיר חלק מהמסמכים.",
  "externalEntry.recognition.alreadySource": "כבר מקור של {codes}",
  "externalEntry.recognition.alreadySourceCount": "כבר מקור לרשומות רפואיות: {count}",
  "externalEntry.recognition.none": "לא ניתן לקרוא אף אחד מהמסמכים שנבחרו.",
  "externalEntry.recognition.checking": "בדיקת המסמכים…",
  "externalEntry.recognition.checkFailed": "לא ניתן היה לבדוק את המסמכים.",
  "externalEntry.recognition.checkAgain": "בדיקה חוזרת",
  "externalEntry.recognition.startFailed": "לא ניתן היה להתחיל את העיבוד.",
  "externalEntry.recognition.startBusy": "חלק מהמסמכים נכללו בינתיים בעיבוד אחר; הרשימה מציגה כעת היכן הם נמצאים.",
  "externalEntry.recognition.startLimit": "יש יותר מסמכים או עמודים ממה שעיבוד אחד קורא.",
  "externalEntry.recognition.startForbidden": "אין לך הרשאה להתחיל עיבוד זה.",
  "externalEntry.recognition.stagesLabel": "שלבים",
  "externalEntry.recognition.documentsTitle": "מסמכים",
  "externalEntry.recognition.ocr.notNeeded": "הטקסט כבר זוהה",
  "externalEntry.recognition.ocr.waiting": "ממתין לזיהוי טקסט",
  "externalEntry.recognition.ocr.running": "הטקסט בזיהוי",
  "externalEntry.recognition.ocr.succeeded": "הטקסט זוהה",
  "externalEntry.recognition.ocr.failed": "זיהוי הטקסט נכשל",
  "externalEntry.recognition.ocr.cancelled": "זיהוי הטקסט בוטל",
  "externalEntry.recognition.ocr.leftOut": "הושמט",
  "externalEntry.recognition.ocrPercent": "הושלם: {percent}%",
  "externalEntry.recognition.documentPages": "עמודים: {pages}",
  "externalEntry.recognition.segmented": "עמודים שסומנו: {done} מתוך {total}",
  "externalEntry.recognition.events": "אירועי טיפול שנמצאו: {count}",
  "externalEntry.recognition.filled": "רשומות שמולאו: {done} מתוך {total}",
  "externalEntry.recognition.executor": "הבקשות לבינה המלאכותית נשלחות בשם: {name}",
  "externalEntry.recognition.attempt": "ניסיון: {attempt}",
  "externalEntry.recognition.failedAt": "נעצר בשלב: {stage}",
  "externalEntry.recognition.failedDocuments": "מסמכים עם שגיאה",
  "externalEntry.recognition.ocrDocumentFailed": "לא ניתן היה לזהות את הטקסט של מסמך זה.",
  "externalEntry.recognition.leftOutNote": "מסמכים שלא ניתן לקרוא את הטקסט שלהם הושמטו; העיבוד קורא את השאר.",
  "externalEntry.recognition.retry": "ניסיון חוזר",
  "externalEntry.recognition.retrying": "מנסה שוב…",
  "externalEntry.recognition.retryFailed": "לא ניתן היה לנסות שוב את העיבוד.",
  "externalEntry.recognition.retryForbidden": "אין לך הרשאה לנסות שוב עיבוד זה.",
  "externalEntry.recognition.error.ocrFailed": "לא ניתן היה לזהות את הטקסט בחלק מהמסמכים.",
  "externalEntry.recognition.error.documentUnreadable": "באף מסמך אין טקסט שניתן לקרוא, או שמסמך השתנה אחרי תחילת העיבוד.",
  "externalEntry.recognition.error.pageLimitExceeded": "לאחר זיהוי הטקסט התברר שיש במסמכים יותר עמודים ממה שעיבוד אחד קורא.",
  "externalEntry.recognition.error.inputTooLarge": "הטקסט גדול מדי כדי שהבינה המלאכותית תקרא אותו בבקשה אחת.",
  "externalEntry.recognition.error.aiFunctionDisabled": "פונקציית הבינה המלאכותית הזו כבויה בהגדרות המרפאה.",
  "externalEntry.recognition.error.aiKeyMissing": "לחשבון שבשמו נשלחות הבקשות אין מפתח גישה לבינה המלאכותית.",
  "externalEntry.recognition.error.accessLost": "לחשבון שבשמו פעל העיבוד אין עוד גישה.",
  "externalEntry.recognition.error.promptUnavailable": "ההנחיה לבינה המלאכותית אינה זמינה או מוגדרת באופן שעיבוד זה אינו יכול להשתמש בו.",
  "externalEntry.recognition.error.providerRejected": "שירות הבינה המלאכותית דחה את הבקשה.",
  "externalEntry.recognition.error.providerQuota": "מכסת הבקשות או היתרה בשירות הבינה המלאכותית נוצלו.",
  "externalEntry.recognition.error.providerUnavailable": "שירות הבינה המלאכותית לא היה זמין, והניסיונות החוזרים האוטומטיים לא עזרו.",
  "externalEntry.recognition.error.retryLimitReached": "הניסיונות החוזרים האוטומטיים נוצלו.",
  "externalEntry.recognition.error.callOutcomeUnknown": "לא ידוע כיצד הסתיימה הבקשה לבינה המלאכותית: ייתכן שכבר התקבלה תשובה ושולם עליה, ולכן הבקשה אינה נשלחת שוב אוטומטית. ניסיון חוזר ישלח אותה שוב.",
  "externalEntry.recognition.error.resultInvalid": "הבינה המלאכותית החזירה תשובה שלא ניתן היה לקרוא.",
  "externalEntry.recognition.error.internalError": "אירעה שגיאה פנימית בשרת.",
  "externalEntry.recognition.error.unknown": "העיבוד נעצר עם שגיאה.",
  "externalEntry.recognition.proposalsTitle": "רשומות מוצעות",
  "externalEntry.recognition.proposalsNone": "הבינה המלאכותית לא מצאה אירועי טיפול במסמכים.",
  "externalEntry.recognition.proposalsPending": "הרשומות המוצעות יופיעו כאן ככל שהבינה המלאכותית תמלא אותן.",
  "externalEntry.recognition.proposalsStopped": "העיבוד נעצר לפני שהציע רשומה כלשהי.",
  "externalEntry.recognition.untitled": "ללא כותרת",
  "externalEntry.recognition.pagesRef": "עמ' {pages}",
  "externalEntry.recognition.pagesRead": "עמודים שנקראו במלואם: {read} מתוך {total}",
  "externalEntry.recognition.warningsCount": "הערות הבינה המלאכותית: {count}",
  "externalEntry.recognition.status.pending": "ממתינה לבדיקה",
  "externalEntry.recognition.status.accepted": "נשמרה",
  "externalEntry.recognition.status.dismissed": "נדחתה",
  "externalEntry.recognition.savedAs": "נשמרה בתור {code}",
  "externalEntry.recognition.open": "פתיחה",
  "externalEntry.recognition.dismiss": "דחייה",
  "externalEntry.recognition.openNamed": "פתיחת הרשומה המוצעת {name}",
  "externalEntry.recognition.dismissNamed": "דחיית הרשומה המוצעת {name}",
  "externalEntry.recognition.dismissFailed": "לא ניתן היה לדחות את הרשומה.",
  "externalEntry.recognition.decidedConflict": "כבר התקבלה החלטה לגבי רשומה זו; מוצג מצבה הנוכחי.",
  "externalEntry.recognition.proposalTitle": "רשומה מוצעת",
  "externalEntry.recognition.warningsTitle": "הערות הבינה המלאכותית",
  "externalEntry.recognition.pagesPartial": "עמודים שנקראו במלואם: {read} מתוך {total}. האירוע ארוך מכדי לקרוא אותו במלואו — יש לבדוק את הרשומה מול המסמכים.",
  "externalEntry.recognition.decidedAccepted": "נשמרה בתור {code}: {name}, {at}",
  "externalEntry.recognition.decidedDismissed": "נדחתה: {name}, {at}",
  "externalEntry.recognition.cancelledReadOnly": "העיבוד בוטל, ולכן לא ניתן עוד לשמור או לדחות רשומה זו.",
  "externalEntry.recognition.unsavedIntent": "רשומה מוצעת — השינויים לא נשמרו",
  "externalEntry.recognition.notFound": "הרשומה המוצעת לא נמצאה",
  "externalEntry.recognition.notFoundDetail": "ייתכן שהעיבוד שלה נמחק.",
  "externalEntry.recognition.loading": "טעינת הרשומה המוצעת…",
  "externalEntry.recognition.stripTitle": "עיבודים שלא הסתיימו",
  "externalEntry.recognition.fileMark": "בעיבוד",
  "externalEntry.recognition.awaiting": "רשומות מוצעות הממתינות לבדיקה: {count}",
}
